import type { MetricTone } from "@common/components/dashboard/metric"
import type { PeriodMonth } from "@common/components/dashboard/period-picker"
import { formatHoursMinutes } from "@common/lib/number"
import { z } from "zod"
import { type Operations, tailOperations } from "./operations"
import {
  LAST_MONTH,
  MONTH_NAMES,
  MONTHS,
  type MonthStats,
  REPORT_SOURCES,
  tailMonths,
} from "./reliability"

export const overviewSearchFields = {
  months: z
    .number()
    .int()
    .min(1)
    .max(MONTHS.length)
    .optional()
    .catch(undefined),
  end: z.number().int().min(0).max(LAST_MONTH).optional().catch(undefined),
  source: z.enum(REPORT_SOURCES).optional().catch(undefined),
}

const overviewSearchSchema = z.object(overviewSearchFields)

export type OverviewSearch = z.infer<typeof overviewSearchSchema>

export type Period = {
  size: number
  from: number
  end: number
}

export const DEFAULT_WINDOW = 3

export function periodOf(search: OverviewSearch): Period {
  const size = search.months ?? DEFAULT_WINDOW
  const end = Math.max(size - 1, search.end ?? LAST_MONTH)
  return { size, from: end - size + 1, end }
}

export function periodSearch(size: number, end: number): OverviewSearch {
  return {
    months: size === DEFAULT_WINDOW ? undefined : size,
    end: end === LAST_MONTH ? undefined : end,
  }
}

export const PERIOD_MONTHS: PeriodMonth[] = MONTHS.map((label, index) => ({
  label,
  name: MONTH_NAMES[index],
}))

export type Tail = {
  registration: string
  operations: Operations
  months: MonthStats[]
}

export function tailOf(registration: string): Tail {
  const operations = tailOperations(registration)
  return {
    registration,
    operations,
    months: tailMonths(registration, operations.availability),
  }
}

export function total(months: MonthStats[]): MonthStats {
  const sum = (pick: (month: MonthStats) => number) =>
    months.reduce((acc, month) => acc + pick(month), 0)
  return {
    days: sum((month) => month.days),
    hours: sum((month) => month.hours),
    departures: sum((month) => month.departures),
    delays: sum((month) => month.delays),
    cancellations: sum((month) => month.cancellations),
    reports: months.flatMap((month) => month.reports),
  }
}

export function fleetMonths(tails: Tail[]): MonthStats[] {
  return MONTHS.map((_, index) =>
    total(tails.map((tail) => tail.months[index])),
  )
}

export function dispatchReliability(stats: MonthStats): number | null {
  if (stats.departures === 0) return null
  const lost = stats.delays + stats.cancellations
  return 100 - (lost / stats.departures) * 100
}

export function percentText(value: number | null): string {
  return value === null ? "" : `${value.toFixed(2)}%`
}

export function reliabilityTone(value: number | null): MetricTone {
  if (value === null) return "neutral"
  if (value >= 99.5) return "success"
  if (value >= 99) return "warning"
  return "error"
}

export function hoursText(hours: number): string {
  return formatHoursMinutes(Math.round(hours * 60))
}
